import './styles.scss'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Col, Form, Row } from 'antd'

import CustomButton from '@/components/CustomButton'
import { NotificationManager } from '@/components/Notification'
import { fapiCustomerService } from '@/services/fapi'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectCountryNames, selectRegions } from '@/store/selectors/configSelector'
import {
  selectCurrentUserEmail,
  selectUserCountry,
  selectUserCountryId,
  selectUsername,
} from '@/store/selectors/userSelector'
import { selectUserId, selectUserType } from '@/store/slices/authenticationSlice'
import {
  selectBillingAddress,
  selectCustomerAddress,
  selectCustomerAddressPartnerNumber,
  selectDeliveryAddresses,
} from '@/store/slices/customerSlice'
import { userActions } from '@/store/slices/userSlice'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useAsync from '@/hooks/useAsync'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useCalculateScrollbarWidth from '@/hooks/useCalculateScrollbarWidth'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { ADDRESS_ACTION_TYPES } from '@/libs/constants/actionTypes'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { MODULE_PROFILE_RIGHTS } from '@/libs/constants/modulerights'
import { RESPONSE_STATUS } from '@/libs/constants/statuses'
import { USER_TYPES } from '@/libs/constants/user'
import { linkGenerator } from '@/libs/utils/language'
import { isFormDirty } from '@/libs/utils/validatorUtils'

import AddressDetailBlock from './components/AddressDetailBlock'
import DeliveryAddressBlock from './components/DeliveryAddressBlock'
import InformationBlock from './components/InformationBlock'
import InvoiceAddress from './components/InvoiceAddress'
import LanguageBlock from './components/LanguageBlock'
import PasswordBlock from './components/PasswordBlock'

const renderZipCodeErrorPopup = (responseError) => {
  const errorCode = Number.parseInt(responseError?.response?.data.errorCode, 10)
  if (errorCode === RESPONSE_STATUS.invalidZip) {
    return NotificationManager.error({
      message: 'checkout.submitErrors.invalidZip',
    })
  }

  return NotificationManager.error({
    message: 'notification.error.addChangeAddress',
  })
}

export default function ProfilePage({ breadcrumbs }) {
  useDocumentTitle('pageTitle.myProfile')
  useBreadcrumbs(breadcrumbs)
  useCalculateScrollbarWidth()

  useAdobeAnalysis(null, {
    pageId: '16',
    pageName: 'my profile',
    pageType: PAGE_TYPES.PROFILE,
  })

  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [form] = Form.useForm()

  const [isAddressDetailBlockShown, setIsAddressDetailBlockShown] = useState(false)
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState({})
  const [actionType, setActionType] = useState(null)

  const userId = useAppSelector(selectUserId)
  const deliveryAddresses = useAppSelector(selectDeliveryAddresses)
  const customerAddress = useAppSelector(selectCustomerAddress)
  const billingAddress = useAppSelector(selectBillingAddress)
  const partnerNumber = useAppSelector(selectCustomerAddressPartnerNumber)
  const email = useAppSelector(selectCurrentUserEmail)
  const username = useAppSelector(selectUsername)
  const country = useAppSelector(selectUserCountry)
  const countryId = useAppSelector(selectUserCountryId)
  const countryNamesConfig = useAppSelector(selectCountryNames)
  const userType = useAppSelector(selectUserType)
  const regions = useAppSelector(selectRegions)

  const countryName = useMemo(() => {
    const foundConfig = countryNamesConfig.find(
      (countryConfig) => countryConfig.value === country,
    )

    return foundConfig ? foundConfig.name : country
  }, [countryNamesConfig, country])

  const resetAddressDetailBlock = useCallback(() => {
    form.setFieldsValue({
      name: '',
      name2: '',
      street: '',
      street4: '',
      houseNumber: '',
      phoneNumber: '',
      streetAndHouseNumber: '',
      zip: '',
      region: '',
      city: '',
      country: countryName,
      defaultAddress: false,
      partnerNumber: '',
      taxNumber: '',
    })
  }, [form, countryName])

  const cancelRequest = useCallback(() => {
    setIsAddressDetailBlockShown(false)
    setActionType(null)
    setSelectedDeliveryAddress({})
    form.resetFields(['deliveryAddress'])
    form.setFieldsValue({
      deliveryAddress: 0,
    })

    resetAddressDetailBlock()
    window.scrollTo({ top: 0 })
  }, [form, resetAddressDetailBlock])

  const submitChangeDeliveryAddress = useCallback((parameters) => {
    return fapiCustomerService.changeDeliveryAddress(parameters)
  }, [])

  const submitAddDeliveryAddress = useCallback((parameters) => {
    return fapiCustomerService.addDeliveryAddress(parameters)
  }, [])

  const submitDeleteDeliveryAddress = (parameters) => {
    return fapiCustomerService.deleteDeliveryAddress(parameters)
  }

  const {
    execute: executeChangeDeliveryAddress,
    status: changeDeliveryAddressStatus,
    error: changeDeliveryAddressError,
  } = useAsync(submitChangeDeliveryAddress, false)
  const {
    execute: executeAddDeliveryAddress,
    status: addDeliveryAddressStatus,
    error: addDeliveryAddressError,
  } = useAsync(submitAddDeliveryAddress, false)
  const {
    execute: executeDeleteDeliveryAddress,
    status: deleteDeliveryAddressStatus,
    error: deleteDeliveryAddressError,
  } = useAsync(submitDeleteDeliveryAddress, false)

  const billingInfo = useMemo(
    () => ({
      ...billingAddress,
      names: [
        billingAddress?.name,
        billingAddress?.name2,
        billingAddress?.name3,
      ],
    }),
    [billingAddress],
  )

  const isAddressDetailLoading = changeDeliveryAddressStatus === 'pending'
    || addDeliveryAddressStatus === 'pending'

  useEffect(() => {
    if (changeDeliveryAddressStatus === 'success') {
      cancelRequest()

      NotificationManager.success({
        message: 'notification.success.addChangeAddress',
      })
    }

    if (changeDeliveryAddressStatus === 'error' && changeDeliveryAddressError) {
      renderZipCodeErrorPopup(changeDeliveryAddressError)
    }
  }, [changeDeliveryAddressStatus, changeDeliveryAddressError, cancelRequest])

  useEffect(() => {
    if (addDeliveryAddressStatus === 'success') {
      cancelRequest()

      NotificationManager.success({
        message: 'notification.success.addChangeAddress',
      })
    }

    if (addDeliveryAddressStatus === 'error' && addDeliveryAddressError) {
      renderZipCodeErrorPopup(addDeliveryAddressError)
    }
  }, [addDeliveryAddressStatus, addDeliveryAddressError, cancelRequest])

  useEffect(() => {
    if (deleteDeliveryAddressStatus === 'success') {
      cancelRequest()

      NotificationManager.success({
        message: 'notification.success.addChangeAddress',
      })
    }

    if (deleteDeliveryAddressError) {
      NotificationManager.error({
        message: 'notification.error.addChangeAddress',
      })
    }
  }, [deleteDeliveryAddressStatus, deleteDeliveryAddressError, cancelRequest])

  const handleDeleteAddress = async () => {
    try {
      await form.validateFields()
      const values = form.getFieldsValue(false)
      if (values.region && regions[values.region]) {
        values.region = regions[values.region].label
      }
      executeDeleteDeliveryAddress(values)
    } catch (error) {
      console.error('handleDeleteAddress: err', error)
    }
  }

  const handleAddAddress = async () => {
    try {
      await form.validateFields()
      const values = form.getFieldsValue(false)

      const bodyRequest = {
        name: values.name,
        name2: values.name2,
        street: values.street,
        street4: values.street4,
        houseNumber: values.houseNumber,
        city: values.city,
        country,
        zip: values.zip,
        region: values.region,
        // phoneNumber: customerAddress.phoneNumber,
        // taxNumber: customerAddress.taxNumber,
        defaultAddress: values.defaultAddress,
      }

      if (bodyRequest.region && regions[bodyRequest.region]) {
        bodyRequest.region = regions[bodyRequest.region].label
      }

      executeAddDeliveryAddress(bodyRequest)
    } catch (error) {
      console.error('handleAddAddress: err', error)
    }
  }

  const handleChangeAddress = async () => {
    try {
      await form.validateFields()
      const values = form.getFieldsValue(false)
      const isDirty = isFormDirty(selectedDeliveryAddress, values)

      if (isDirty) {
        const bodyRequest = {
          name: values.name,
          name2: values.name2,
          street: values.street,
          street4: values.street4,
          houseNumber: values.houseNumber,
          city: values.city,
          country,
          zip: values.zip,
          region: values.region,
          partnerNumber: values.partnerNumber,
          phoneNumber: values.phoneNumber,
          taxNumber: values.taxNumber,
          defaultAddress: values.defaultAddress,
        }
        if (bodyRequest.region && regions[bodyRequest.region]) {
          bodyRequest.region = regions[bodyRequest.region].label
        }
        executeChangeDeliveryAddress(bodyRequest)
      }
    } catch (error) {
      console.error('handleChangeAddress: err', error)
    }
  }

  const handleDeliveryAddressChange = (selectedIndex) => {
    if (selectedIndex > 0) {
      setIsAddressDetailBlockShown(true)
      setActionType(ADDRESS_ACTION_TYPES.CHANGE_ADDRESS)

      const selectedAddress = deliveryAddresses[selectedIndex - 1]
      const foundCountryNameConfig = countryNamesConfig.find(
        (countryConfig) => countryConfig.value === selectedAddress.country,
      )

      const selectedCountryName = foundCountryNameConfig
        ? foundCountryNameConfig.name
        : selectedAddress.country

      const selectedValues = {
        name: selectedAddress.name,
        name2: selectedAddress.name2,
        street: selectedAddress.street,
        street4: selectedAddress.street4,
        houseNumber: selectedAddress.houseNumber,
        zip: selectedAddress.zip,
        region: selectedAddress.region,
        city: selectedAddress.city,
        country: selectedCountryName,
        partnerNumber: selectedAddress.partnerNumber,
        taxNumber: selectedAddress.taxNumber,
        phoneNumber: selectedAddress.phoneNumber,
        defaultAddress: selectedAddress.defaultAddress || false,
      }
      form.setFieldsValue(selectedValues)
      setSelectedDeliveryAddress(selectedValues)
    } else {
      setIsAddressDetailBlockShown(false)
      setActionType(null)
    }
  }

  const handleChangeLanguage = (newLanguage, initialLangCode) => {
    dispatch(
      userActions.requestChangeLanguage({
        userId,
        newLanguage,
        countryId,
        initialLangCode,
      }),
    )
  }

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      validateMessages={{
        required: t('myProfile.form.validateMessages.required'),
        string: {
          max: t('errors.maxInputExceeded'),
        },
      }}
    >
      <div className="profile__wrapper">
        <InformationBlock
          email={email}
          partnerNumber={partnerNumber}
          username={username}
        />
        <Row gutter={[12, 24]}>
          <Col lg={12} xs={24}>
            <DeliveryAddressBlock
              customerAddress={customerAddress}
              deliveryAddresses={deliveryAddresses}
              handleDeliveryAddressChange={handleDeliveryAddressChange}
            />
          </Col>
          <Col lg={{ span: 8, offset: 2 }} xl={{ span: 8, offset: 4 }} xs={24}>
            <InvoiceAddress billingInfo={billingInfo} />
          </Col>
        </Row>
        <Row gutter={[12, 24]}>
          <Col lg={12} xs={24}>
            <div className="profile__main-block__buttons">
              <PermissionWrapper
                permission={MODULE_PROFILE_RIGHTS.REQUEST_DELIVERY_ADDRESS_CHANGE}
              >
                <CustomButton
                  disabled={isAddressDetailBlockShown}
                  type="primary"
                  onClick={() => {
                    setIsAddressDetailBlockShown(true)
                    setActionType(ADDRESS_ACTION_TYPES.ADD_NEW_ADDRESS)
                    resetAddressDetailBlock()
                  }}
                >
                  {t('myProfile.addANewAddress')}
                </CustomButton>
              </PermissionWrapper>
            </div>
            {isAddressDetailBlockShown
              ? (
                <AddressDetailBlock
                  actionType={actionType}
                  cancelRequest={cancelRequest}
                  handleAddAddress={handleAddAddress}
                  handleChangeAddress={handleChangeAddress}
                  handleDeleteAddress={handleDeleteAddress}
                  loading={isAddressDetailLoading}
                />
              )
              : null}
          </Col>
          <Col lg={{ span: 8, offset: 2 }} xl={{ span: 8, offset: 4 }} xs={24}>
            <Row gutter={[24, { xs: 24, sm: 24, md: 50, lg: 70 }]}>
              <PermissionWrapper
                permission={MODULE_PROFILE_RIGHTS.REQUEST_INVOICE_ADDRESS_CHANGE}
              >
                <Col span={24}>
                  <CustomButton
                    type="primary"
                    onClick={() => navigate(linkGenerator('/profile/change-invoice-address'))}
                  >
                    {t('myProfile.requestInvoiceChanges')}
                  </CustomButton>
                </Col>
              </PermissionWrapper>
              {userType === USER_TYPES.CUSTOMER_USER
                ? (
                  <PermissionWrapper
                    permission={MODULE_PROFILE_RIGHTS.RESET_OWN_PASSWORD}
                  >
                    <Col span={24}>
                      <PasswordBlock />
                    </Col>
                  </PermissionWrapper>
                )
                : null}
              <Col span={24}>
                <LanguageBlock onChangeLanguage={handleChangeLanguage} />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </Form>
  )
}
